import {
  DEFAULT_STORE_CODE,
  MAGENTO_GRAPHQL_CAROUSEL_TYPES,
  REVALIDATE_TIME,
  WIZZY_CONSTANTS,
} from '@/constants';
import { ProductItemPropTypes } from '@/components/Card/Product';
import {
  wizzySearchApi,
  wizzyFilter,
  wizzyTrendingSearchesApi,
  getProductsByIds,
  wizzyAutoCompleteApi,
} from './wizzyApis';
import { logger, storage } from '@/config';
import { PRODUCT_TYPE_ENUMS } from '@/features/ProductDetails/constants/ProductType';
import { getMagentoCarouselsGraphQl } from '@/services';
import { getKeyValue } from './helper';
interface Category {
  image: string;
  level: number;
  name: string;
  id: string;
  position: number;
  url: string;
  parentId: string;
}

interface AttributeValue {
  variationId: string;
  inStock: boolean;
  value: string[];
}

interface Attribute {
  values: AttributeValue[];
  name: string;
  id: string;
}
interface WizzyProduct {
  groupId: string;
  finalPrice: number;
  discount: number;
  colors: string[];
  discountPercentage: number;
  sellingPrice: number;
  totalReviews: number;
  sizes: any[];
  avgRatings: number;
  price: number;
  inStock: boolean;
  id: string;
  categories: Category[];
  sku: string[];
  hoverImage: string;
  brand: string;
  images: string[];
  url: string;
  mainImage: string;
  name: string;
  attributes: Attribute[];
  allVariantsGroup: string[];
  swatches?: any;
  isFavouritesProduct?: boolean;
}

type GetWizzyFilterDataTypes = {
  urlKey: string;
  pageIndex?: string | number;
  pageSize?: string | number;
  appliedFilters?: any;
  appliedSort?: any;
  searchId?: string;
  attributes?: any;
  inStock?: boolean[];
  q?: string;
  type?:
    | 'DEFAULT'
    | 'CATEGORY_PAGE'
    | 'AUTOCOMPLETE_OPTION'
    | 'AUTOCOMPLETE_DEFAULT'
    | 'NO_RESULTS_FOUND';
  useNextCache?: boolean;
  headers?: any;
  makeYourHamperPage?: boolean;
};

type GetWizzySearchFilterDataTypes = {
  categories: string[];
  pageIndex?: string | number;
  pageSize?: string | number;
  appliedFilters?: any;
  appliedSort?: any;
  searchId?: string;
  attributes?: any;
  inStock?: boolean[];
  q?: string;
  type?:
    | 'DEFAULT'
    | 'CATEGORY_PAGE'
    | 'AUTOCOMPLETE_OPTION'
    | 'AUTOCOMPLETE_DEFAULT'
    | 'NO_RESULTS_FOUND';
  useNextCache?: boolean;
  headers?: any;
  page?: string;
};
interface AttributeValue {
  variationId: string;
  inStock: boolean;
  value: string[];
}

interface Attribute {
  values: AttributeValue[];
  name: string;
  id: string;
}

interface CustomAttribute {
  attribute_code: string;
  value: string;
}

interface WizzyPropTypes {
  numberOfRecommendations?: number;
  endpoint?: string;
  filters?: {
    groupType?: string;
  };
}

interface CartRecommendationsPropTypes extends WizzyPropTypes {
  cartItems: ProductItemPropTypes[] | null;
}

export function convertToCustomAttributes(
  attributes: Attribute[],
  isGetProductsById: boolean
): CustomAttribute[] {
  return attributes?.map(attribute => {
    const attributeValue = isGetProductsById
      ? attribute?.values[0]
      : attribute?.values[0]?.value?.[0] || '';
    return {
      attribute_code: attribute.id,
      value: attributeValue,
    };
  }) as CustomAttribute[];
}

function moveValueToTop(
  values: { variationId: string; inStock: boolean; value: string }[],
  targetValue: string
) {
  // Find the index of the object with the matching value
  const index = values.findIndex(item => item.value === targetValue);

  // If found, move the object to the top (index 0)
  if (index !== -1) {
    const [matchedItem] = values.splice(index, 1); // Remove the item
    values.unshift(matchedItem); // Add it to the start of the array
  }

  return values;
}

export function structureProductsData(
  products: WizzyProduct[],
  isGetProductsById = false
) {
  const res = products?.map((item: WizzyProduct) => {
    const custom_attributes = convertToCustomAttributes(
      item?.attributes,
      isGetProductsById
    );
    const attributes = getKeyValue(custom_attributes, 'attribute_code');
    const urlToRemove =
      process.env.NEXT_PUBLIC_CDN_LINK + '/media/catalog/product';
    const cacheRegex = /\/cache\/[^/]+\//;
    const thumbnail = item?.mainImage
      ?.replace(urlToRemove, '')
      ?.replace(cacheRegex, '/');
    const isConfigurableProduct = !item?.isFavouritesProduct
      ? item?.swatches?.sizes?.values?.length > 1 && item?.groupId !== ''
      : item?.groupId !== '';
    if (isConfigurableProduct) {
      const sizes = moveValueToTop(
        item?.swatches?.sizes?.values || item?.sizes,
        item?.sizes[0]?.value || item?.sizes[0]?.value
      );
      return {
        id: parseInt(item?.id),
        groupId: item?.groupId || item?.id,
        name: item?.name,
        type_id: PRODUCT_TYPE_ENUMS.configurable,
        sku: attributes?.sku,
        stock_status: !item?.inStock ? 'OUT_OF_STOCK' : 'IN_STOCK',
        price: item?.price !== 0 ? item?.price : item?.sellingPrice,
        productByWizzy: true,
        sizes,
        custom_attributes: [
          ...custom_attributes,
          {
            attribute_code: 'thumbnail',
            value: thumbnail,
          },
          {
            attribute_code: 'url_key',
            value: process.env.WIZZY_PRODUCT_URL
              ? item?.url.replace(process.env.WIZZY_PRODUCT_URL, '')
              : item?.url,
          },
          ...(item?.discount > 0 // If the item has actual price, add special price
            ? [
                {
                  attribute_code: 'special_price',
                  value: item?.sellingPrice,
                },
              ]
            : []),
        ],
      };
    }
    return {
      id: parseInt(item?.id),
      name: item?.name,
      sku: attributes?.sku,
      typeId: PRODUCT_TYPE_ENUMS.simple,
      stock_status: !item?.inStock ? 'OUT_OF_STOCK' : 'IN_STOCK',
      price: item?.price !== 0 ? item?.price : item?.sellingPrice,
      productByWizzy: true,
      custom_attributes: [
        ...custom_attributes,
        {
          attribute_code: 'thumbnail',
          value: thumbnail,
        },
        {
          attribute_code: 'url_key',
          value: process.env.WIZZY_PRODUCT_URL
            ? item?.url.replace(process.env.WIZZY_PRODUCT_URL, '')
            : item?.url,
        },
        ...(item?.discount > 0 // If the item has actual price, add special price
          ? [
              {
                attribute_code: 'special_price',
                value: item?.sellingPrice,
              },
            ]
          : []),
      ],
    };
  });
  return res;
}

const convertCategoryData = (categoryData: any) => {
  const childCategories = categoryData?.map((category: any) => {
    return {
      name: category?.data.name,
      image: category?.data.image,
      position: category?.data.position,
      url_key: category?.data.id,
      urlKey: category?.data.id,
    };
  });
  childCategories?.sort((a: any, b: any) => a.position - b.position);
  return childCategories;
};

function generateChildCategoriesFilter(categoryData: any) {
  const childCategoriesFilters = categoryData?.map((category: any) => {
    return {
      label: category?.name,
      value: category?.urlKey,
    };
  });

  return childCategoriesFilters;
}

export async function getAllListingWizzyData({
  currentCategoryUrlKey,
  appliedFilters,
  appliedSort,
  useNextCache = false,
  headers = {},
  makeYourHamperPage = false,
}: {
  currentCategoryUrlKey: string;
  appliedFilters: any;
  appliedSort: any;
  useNextCache?: boolean;
  headers?: Record<string, string | undefined>;
  makeYourHamperPage?: boolean;
}) {
  // Construct base request body
  const baseRequestBody = {
    pageSize: 1,
    appliedFilters,
    appliedSort,
    ...(Object.keys(headers).length > 0 && {
      headers,
      useNextCache: useNextCache || false,
    }),
    makeYourHamperPage: makeYourHamperPage || false,
  };

  // Fetch current category data
  const currentCategory = await getWizzyPlpData({
    ...baseRequestBody,
    urlKey: currentCategoryUrlKey,
  });

  // Fetch child categories' data concurrently
  const childCategories = currentCategory?.childCategories || [];
  const childCategoryRequests = childCategories.map((childCategory: any) =>
    getWizzyPlpData({
      ...baseRequestBody,
      urlKey: childCategory.urlKey,
      pageSize: 6,
    }).then((res: any) =>
      res?.products?.length ? { ...res, urlKey: childCategory.urlKey } : null
    )
  );

  // Wait for all child category requests and filter out null results
  const childCategoriesWithProducts = (
    await Promise.all(childCategoryRequests || [])
  ).filter(Boolean);
  return {
    ...currentCategory,
    childCategoriesWithProducts,
  };
}

export const getWizzyPlpData: any = async ({
  urlKey = 'shop',
  pageIndex = 1,
  pageSize = 30,
  appliedFilters,
  appliedSort,
  searchId,
  attributes,
  inStock = [true, false],
  q,
  type = 'CATEGORY_PAGE',
  useNextCache = false,
  headers = {},
  makeYourHamperPage = false,
}: GetWizzyFilterDataTypes) => {
  const requestBody: any = {
    page: pageIndex,
    productsCount: pageSize,
    currency: 'INR',
    type: type,
    attributeFacetValuesLimit: WIZZY_CONSTANTS.FACETS_VALUE_LIMIT,
    includeCategoriesFacetForPage: 'true',
    inStock: inStock,
    [type === 'DEFAULT' ? 'categories:all' : 'categories']: [urlKey],
    facets: [
      {
        key: WIZZY_CONSTANTS.FACETS.CATEGORIES,
        label: 'Filter By Category',
        config: { limit: 200 },
      },
      {
        key: WIZZY_CONSTANTS.FACETS.PRICE,
        label: 'Filter By Price',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.LIFESTYLE,
        label: 'Filter By Lifestyle',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.TEXTURE,
        label: 'Filter By Texture',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.FLAVOUR,
        label: 'Filter By Flavour',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.SKILL,
        label: 'Filter By Skill',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.CUISINE,
        label: 'Filter By Cuisine',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.ADDITIONAL_FILTERS,
        label: 'Additional Filters',
      },
      { key: WIZZY_CONSTANTS.FACETS.HALAL, label: 'Halal' },
      { key: WIZZY_CONSTANTS.FACETS.CHOICE_OF_MEAT, label: 'Choice of Meat' },
      { key: WIZZY_CONSTANTS.FACETS.ORIGIN, label: 'Origin' },
      { key: WIZZY_CONSTANTS.FACETS.PREPARATION, label: 'Preparation' },
    ],
    swatch: [{ key: 'sizes' }],
    getAllVariants: false,
    makeYourHamperPage,
  };

  if (appliedFilters) {
    if (!appliedFilters?.categories) {
      requestBody.attributes = appliedFilters;
      if (requestBody.makeYourHamperPage) {
        requestBody.attributes['available_for_gift_hampers'] = ['Yes'];
      }
    } else {
      const newAttributes = {
        ...appliedFilters,
      };
      if (requestBody.makeYourHamperPage) {
        newAttributes['available_for_gift_hampers'] = ['Yes'];
      }
      requestBody.attributes = newAttributes;
      delete requestBody.attributes.categories;

      const categoryToBeFiltered =
        type === 'DEFAULT'
          ? [urlKey, ...appliedFilters.categories]
          : [...appliedFilters.categories];
      requestBody[type === 'DEFAULT' ? 'categories:all' : 'categories'] =
        categoryToBeFiltered;
    }
  }
  if (appliedSort) {
    if (appliedSort.field === WIZZY_CONSTANTS.SORT.SELLING_PRICE) {
      if (appliedSort?.order === 'asc') {
        requestBody.sort =
          WIZZY_CONSTANTS?.SORT_OPTIONS?.SELLING_PRICE?.ASCENDING;
      } else if (appliedSort?.order === 'desc') {
        requestBody.sort =
          WIZZY_CONSTANTS?.SORT_OPTIONS?.SELLING_PRICE?.DESCENDING;
      }
    } else if (appliedSort.field === WIZZY_CONSTANTS.SORT.NAME) {
      if (appliedSort?.order === 'asc') {
        requestBody.sort = WIZZY_CONSTANTS?.SORT_OPTIONS?.NAME?.ASCENDING;
      } else if (appliedSort?.order === 'desc') {
        requestBody.sort = WIZZY_CONSTANTS?.SORT_OPTIONS?.NAME?.DESCENDING;
      }
    }
  } else {
    const catgeoryPositionSort = 'wz_' + urlKey + '_order:float';
    requestBody.sort = [
      ...WIZZY_CONSTANTS.SORT_OPTIONS.DEFAULT,
      { field: catgeoryPositionSort, order: 'asc' },
    ];
  }
  if (searchId) {
    requestBody.searchedKey = searchId;
  }
  if (q) {
    requestBody.q = q;
  }

  if (attributes) {
    if (requestBody.attributes) {
      requestBody.attributes = { ...requestBody.attributes, ...attributes };
    } else {
      requestBody.attributes = attributes;
    }
  }

  const res = await wizzyFilter(requestBody, {
    headers,
    useNextCache: useNextCache,
  });

  const response = res?.payload;

  const categoryFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.CATEGORIES
  )?.data;

  const lifestyleFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.LIFESTYLE
  );

  const textureFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.TEXTURE
  );

  const flavourFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.FLAVOUR
  );

  const cuisineFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.CUISINE
  );

  const skillFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.SKILL
  );

  const additionalFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.ADDITIONAL_FILTERS
  );

  const categoriesData = categoryFilters?.filter(
    (category: any) => category?.data.parentId === urlKey
  );

  const parentCategoryData = categoryFilters?.find(
    (category: any) => category?.key === urlKey
  );

  const products = structureProductsData(response?.result) || [];

  const categoryData = convertCategoryData(categoriesData);
  const childCategoriesFilters = generateChildCategoriesFilter(categoryData);
  const metaData = {
    childCategories: categoryData,
    name: parentCategoryData?.label,
    url_key: parentCategoryData?.key,
  };
  const sortApplied = response?.filters?.sort?.filter(
    (field: { field: string }) =>
      field.field !== 'relevance' && field.field !== 'inStock'
  );

  const filtersApplied = response?.filters?.attributes;

  const allFacets = response?.facets || [];

  const excludedFacetKeys = [
    WIZZY_CONSTANTS.FACETS.CATEGORIES,
    WIZZY_CONSTANTS.FACETS.PRICE,
  ];

  const dynamicFilters =
    allFacets && allFacets?.length > 0
      ? allFacets.filter((facet: any) => !excludedFacetKeys.includes(facet.key))
      : [];

  const data = {
    categoryFilters,
    dynamicFilters,
    childCategories: categoryData,
    metaData,
    childCategoriesFilters,
    currentCategory: parentCategoryData,
    lifestyleFilters,
    textureFilters,
    flavourFilters,
    cuisineFilters,
    skillFilters,
    additionalFilters,
    products,
    totalCount: response?.total,
    pageIndex: response?.filters?.page,
    pagesAvailable: response?.pages,
    searchId: response?.filters?.searchedKey,
    filtersApplied,
    sortApplied,
    responseId: res?.responseId,
  };
  return data;
};

export const getWizzySearchFiltersData: any = async ({
  categories,
  pageIndex = 1,
  pageSize = 30,
  appliedFilters,
  appliedSort,
  searchId,
  attributes,
  inStock = [true, false],
  q,
  type = 'CATEGORY_PAGE',
  useNextCache = false,
  headers = {},
}: GetWizzySearchFilterDataTypes) => {
  const requestBody: any = {
    page: pageIndex,
    productsCount: pageSize,
    currency: 'INR',
    type: type,
    attributeFacetValuesLimit: WIZZY_CONSTANTS.FACETS_VALUE_LIMIT,
    includeCategoriesFacetForPage: 'true',
    inStock: inStock,
    categories: categories,
    facets: [
      {
        key: WIZZY_CONSTANTS.FACETS.CATEGORIES,
        label: 'Filter By Category',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.LIFESTYLE,
        label: 'Filter By Lifestyle',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.TEXTURE,
        label: 'Filter By Texture',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.FLAVOUR,
        label: 'Filter By Flavour',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.SKILL,
        label: 'Filter By Skill',
      },
      {
        key: WIZZY_CONSTANTS.FACETS.CUISINE,
        label: 'Filter By Cuisine',
      },
    ],
    swatch: [{ key: 'sizes' }],
    getAllVariants: false,
  };
  if (appliedFilters) {
    if (!appliedFilters?.categories) {
      requestBody.attributes = appliedFilters;
    } else {
      const newAttributes = {
        ...appliedFilters,
      };
      requestBody.attributes = newAttributes;
      delete requestBody.attributes.categories;
      requestBody.categories = [...appliedFilters.categories];
    }
  }
  if (appliedSort) {
    if (appliedSort.field === WIZZY_CONSTANTS.SORT.SELLING_PRICE) {
      if (appliedSort?.order === 'asc') {
        requestBody.sort =
          WIZZY_CONSTANTS?.SORT_OPTIONS?.SELLING_PRICE?.ASCENDING;
      } else if (appliedSort?.order === 'desc') {
        requestBody.sort =
          WIZZY_CONSTANTS?.SORT_OPTIONS?.SELLING_PRICE?.DESCENDING;
      }
    } else if (appliedSort.field === WIZZY_CONSTANTS.SORT.NAME) {
      if (appliedSort?.order === 'asc') {
        requestBody.sort = WIZZY_CONSTANTS?.SORT_OPTIONS?.NAME?.ASCENDING;
      } else if (appliedSort?.order === 'desc') {
        requestBody.sort = WIZZY_CONSTANTS?.SORT_OPTIONS?.NAME?.DESCENDING;
      }
    }
  } else {
    requestBody.sort = WIZZY_CONSTANTS?.SORT_OPTIONS?.DEFAULT;
  }
  if (searchId) {
    requestBody.searchedKey = searchId;
  }
  if (q) {
    requestBody.q = q;
  }

  if (attributes) {
    if (requestBody.attributes) {
      requestBody.attributes = { ...requestBody.attributes, ...attributes };
    } else {
      requestBody.attributes = attributes;
    }
  }

  const res = await wizzyFilter(requestBody, {
    headers,
    useNextCache: useNextCache,
  });

  const response = res?.payload;

  const categoryFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.CATEGORIES
  )?.data;

  const lifestyleFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.LIFESTYLE
  );

  const textureFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.TEXTURE
  );

  const flavourFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.FLAVOUR
  );

  const cuisineFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.CUISINE
  );

  const skillFilters = response?.facets?.find(
    (facet: any) => facet.key === WIZZY_CONSTANTS.FACETS.SKILL
  );

  const categoriesData = categoryFilters?.filter(
    (category: any) => category?.data.parentId === categories
  );

  const parentCategoryData = categoryFilters?.find(
    (category: any) => category?.key === categories
  );

  const products = structureProductsData(response?.result) || [];

  const { saleableProducts = products, nonSaleableCount = 0 } = products.reduce(
    (acc: any, product) => {
      const customAttributes = getKeyValue(
        product?.custom_attributes,
        'attribute_code'
      );
      const wzAttributeSetId =
        customAttributes?.wz_attribute_set_id?.toLowerCase();

      if (
        wzAttributeSetId === 'default' ||
        wzAttributeSetId === 'gift hampers'
      ) {
        acc.saleableProducts.push(product);
      } else {
        acc.nonSaleableCount += 1;
      }

      return acc;
    },
    { saleableProducts: [], nonSaleableCount: 0 }
  );

  const categoryData = convertCategoryData(categoriesData);
  const childCategoriesFilters = generateChildCategoriesFilter(categoryData);
  const metaData = {
    childCategories: categoryData,
    name: parentCategoryData?.label,
    url_key: parentCategoryData?.key,
  };
  const sortApplied = response?.filters?.sort?.filter(
    (field: { field: string }) =>
      field.field !== 'relevance' && field.field !== 'inStock'
  );

  const filtersApplied = response?.filters?.attributes;

  const data = {
    childCategories: categoryData,
    metaData,
    childCategoriesFilters,
    currentCategory: parentCategoryData,
    lifestyleFilters,
    textureFilters,
    flavourFilters,
    cuisineFilters,
    skillFilters,
    products: saleableProducts,
    totalCount: response?.total - nonSaleableCount,
    pageIndex: response?.filters?.page,
    pagesAvailable: response?.pages,
    searchId: response?.filters?.searchedKey,
    filtersApplied,
    sortApplied,
    responseId: res?.responseId,
  };
  return data;
};

interface StructureFiltersPropTypes {
  categories?: string[];
  lifestyle?: string[];
  texture?: string[];
  flavour?: string[];
  skill_level_tag?: string[];
  cuisine?: string[];
  additional_filters?: string[];
  nonveg_popularity_tag?: string[];
}

// The input type can be generic, as it's your searchParams object
type SearchParams = { [key: string]: string | string[] | undefined };

// The output type will be a mapping of any API key to a string array
type DynamicFilters = { [key: string]: string[] };

export const structureDynamicFilters = (
  searchParams: SearchParams
): DynamicFilters => {
  // 1. Kept your excellent normalizeFilter helper
  const normalizeFilter = (
    filter: string | string[] | undefined
  ): string[] | undefined => {
    if (typeof filter === 'string') {
      return [filter];
    } else if (Array.isArray(filter)) {
      return filter;
    }
    return undefined;
  };

  // 2. Initialize the result object
  const result: DynamicFilters = {};
  const filterPrefix = 'filter_';

  // 3. Iterate over all entries in the searchParams object
  for (const [key, value] of Object.entries(searchParams)) {
    // 4. Check if the key starts with our filter prefix
    if (key.startsWith(filterPrefix)) {
      // 5. Normalize the value (e.g., 'item1' -> ['item1'])
      const normalizedValue = normalizeFilter(value);

      // 6. Get the API key by removing the prefix
      // e.g., 'filter_categories' becomes 'categories'
      // e.g., 'filter_nonveg_bone_type' becomes 'nonveg_bone_type'
      const apiKey = key.substring(filterPrefix.length);

      // 7. If the value is valid and the API key is not empty, add it
      if (normalizedValue && apiKey) {
        result[apiKey] = normalizedValue;
      }
    }
    // Any key that doesn't start with 'filter_' (like 'sort' or 'page') is ignored.
  }

  // 8. Return the dynamically built object
  return result;
};

export const structureFilters = ({
  filter_categories,
  filter_lifestyle,
  filter_texture,
  filter_flavour,
  filter_skills,
  filter_cuisine,
  filter_additional,
  filter_nonveg_popularity_tag,
}: {
  filter_categories?: string | string[];
  filter_lifestyle?: string | string[];
  filter_texture?: string | string[];
  filter_flavour?: string | string[];
  filter_skills?: string | string[];
  filter_cuisine?: string | string[];
  filter_additional?: string | string[];
  filter_nonveg_popularity_tag?: string | string[];
}): StructureFiltersPropTypes => {
  const normalizeFilter = (
    filter: string | string[] | undefined
  ): string[] | undefined => {
    if (typeof filter === 'string') {
      return [filter];
    } else if (Array.isArray(filter)) {
      return filter;
    }
    return undefined;
  };

  const result: StructureFiltersPropTypes = {};

  const categories = normalizeFilter(filter_categories);
  const lifestyle = normalizeFilter(filter_lifestyle);
  const texture = normalizeFilter(filter_texture);
  const flavour = normalizeFilter(filter_flavour);
  const skill_level_tag = normalizeFilter(filter_skills);
  const cuisine = normalizeFilter(filter_cuisine);
  const additional_filters = normalizeFilter(filter_additional);
  const nonveg_popularity_tag = normalizeFilter(filter_nonveg_popularity_tag);

  if (categories) result.categories = categories;
  if (lifestyle) result.lifestyle = lifestyle;
  if (texture) result.texture = texture;
  if (flavour) result.flavour = flavour;
  if (skill_level_tag) result.skill_level_tag = skill_level_tag;
  if (cuisine) result.cuisine = cuisine;
  if (additional_filters) result.additional_filters = additional_filters;
  if (nonveg_popularity_tag)
    result.nonveg_popularity_tag = nonveg_popularity_tag;

  return result;
};
interface FacetsPropTypes {
  key: string;
  label: string;
  order?: number;
  position?: 'left' | 'right';
  buckets?: number[];
  config?: {
    interval: number;
  };
  inventorySources?: {
    key?: string;
    inStock?: any[];
  };
}

interface WizzySearchPropTypes {
  query: string;
  headers: any;
  filters?: {
    categories?: string[];
    lifestyle?: string[];
    texture?: string[];
    flavour?: string[];
  };
  facets?: FacetsPropTypes;
  searchType?: 'autocomplete' | 'direct';
}

export async function wizzySearch({
  query,
  headers,
  searchType = 'direct',
}: WizzySearchPropTypes) {
  try {
    const productsSearchFunction =
      searchType === 'autocomplete'
        ? wizzyAutoCompleteApi({
            q: query,
            suggestionsCount: 10,
            productsCount: 6,
            minQueryLength: 2,
            swatch: JSON.stringify([{ key: 'sizes' }]),
            includeOutOfStock: 'true',
            sort: JSON.stringify([
              {
                field: 'relevance',
                order: 'asc',
                isForVariation: 'false',
              },
              {
                field: 'is_hero_product',
                order: 'desc',
                isForVariation: 'false',
              },
              {
                field: 'inStock',
                order: 'asc',
                isForVariation: 'true',
                isForParent: 'false',
              },
              {
                field: 'sellingPrice',
                order: 'asc',
                isForVariation: 'true',
                isForParent: 'false',
              },
            ]),
            getAllVariants: 'false',
          })
        : wizzySearchApi(
            {
              q: query,
              includeOutOfStock: 'true',
              minQueryLength: 1,
              productsCount: 50,
              swatch: JSON.stringify([{ key: 'sizes' }]),
              sort: JSON.stringify([
                {
                  field: 'relevance',
                  order: 'asc',
                  isForVariation: 'false',
                },
                {
                  field: 'is_hero_product',
                  order: 'desc',
                  isForVariation: 'false',
                },
                {
                  field: 'inStock',
                  order: 'asc',
                  isForVariation: 'true',
                  isForParent: 'false',
                },
                {
                  field: 'sellingPrice',
                  order: 'asc',
                  isForVariation: 'true',
                  isForParent: 'false',
                },
              ]),
              getAllVariants: 'false',
              facets: JSON.stringify([
                {
                  key: WIZZY_CONSTANTS.FACETS.CATEGORIES,
                  label: 'Filter By Category',
                },
                {
                  key: WIZZY_CONSTANTS.FACETS.LIFESTYLE,
                  label: 'Filter By Lifestyle',
                },
                {
                  key: WIZZY_CONSTANTS.FACETS.TEXTURE,
                  label: 'Filter By Texture',
                },
                {
                  key: WIZZY_CONSTANTS.FACETS.FLAVOUR,
                  label: 'Filter By Flavour',
                },
                {
                  key: WIZZY_CONSTANTS.FACETS.SKILL,
                  label: 'Filter By Skill',
                },
                {
                  key: WIZZY_CONSTANTS.FACETS.CUISINE,
                  label: 'Filter By Cuisine',
                },
              ]),
            },
            { headers }
          );
    const [
      { payload, responseId },
      recipes = { products: [], totalCount: 0 },
      stories = { products: [], totalCount: 0 },
    ] = await Promise.all([
      productsSearchFunction,
      searchType === 'direct'
        ? getWizzyPlpData({
            q: query,
            urlKey: 'recipes',
            type: 'DEFAULT',
            pageSize: 50,
            headers,
          })
        : Promise.resolve({ products: [], totalCount: 0 }),
      searchType === 'direct'
        ? getWizzyPlpData({
            q: query,
            urlKey: 'our-stories',
            type: 'DEFAULT',
            pageSize: 50,
            headers,
          })
        : Promise.resolve({ products: [], totalCount: 0 }),
    ]);
    const products =
      searchType === 'autocomplete'
        ? payload?.products?.result
        : payload?.result;
    const responseObject =
      searchType === 'autocomplete' ? payload?.products : payload;
    const autocompleteCategories =
      searchType === 'autocomplete' && payload?.categories;
    const structuredProducts = structureProductsData(products || []);
    const { saleableProducts, nonSaleableCount } = structuredProducts.reduce(
      (acc: any, product) => {
        const customAttributes = getKeyValue(
          product?.custom_attributes,
          'attribute_code'
        );
        const wzAttributeSetId =
          customAttributes?.wz_attribute_set_id?.toLowerCase();

        if (
          wzAttributeSetId === 'default' ||
          wzAttributeSetId === 'gift hampers'
        ) {
          acc.saleableProducts.push(product);
        } else {
          acc.nonSaleableCount += 1;
        }

        return acc;
      },
      { saleableProducts: [], nonSaleableCount: 0 }
    );
    const categories = [
      ...new Map(
        products?.result
          ?.flatMap((product: any) =>
            product?.categories?.filter(
              (category: any) => category?.level === 3
            )
          )
          ?.map((category: any) => [
            category?.id,
            { label: category?.name, value: category?.id },
          ])
      ).values(),
    ];
    const totalProductsCount =
      searchType === 'autocomplete' ? payload?.products?.total : payload?.total;

    return {
      products: saleableProducts,
      autocompleteCategories,
      recipes: recipes?.products || [],
      stories: stories?.products || [],
      totalCount: {
        products: (totalProductsCount || 0) - nonSaleableCount,
        recipes: recipes?.totalCount || 0,
        stories: stories?.totalCount || 0,
      },
      searchedKeyProducts: responseObject?.filters?.searchedKey || null,
      pagesAvailable: {
        products: responseObject?.pages || 0,
      },
      searchResponseId: responseId,
      categories,
      facets: responseObject?.facets,
    };
  } catch (e) {
    logger.log('Error while fetching Wizzy Search Request', e);
    return null;
  }
}

export async function getWizzyTrendingSearches() {
  const options = {
    headers: {
      'x-store-id': storage.get('wizzy_store_id') || process.env.WIZZY_STORE_ID,
      'x-api-key': storage.get('wizzy_api_key') || process.env.WIZZY_API_KEY,
      'x-wizzy-sessionId': sessionStorage.getItem('wizzy_sessionId'),
      'x-wizzy-userId': storage.get('wizzy_userId'),
    },
    next: {
      revalidate: REVALIDATE_TIME,
    },
  };
  const res = await wizzyTrendingSearchesApi(options);
  return res?.queries;
}

export const getWizzyRecommededData: any = async ({
  categories,
  pageIndex = 1,
  pageSize = 12,
  type = 'CATEGORY_PAGE',
  headers = {},
  useNextCache = false,
}: any) => {
  const requestBody: any = {
    sort: [
      {
        field: 'relevance',
        order: 'asc',
      },
      { field: 'is_hero_product', order: 'desc' },
      {
        field: 'inStock',
        order: 'asc',
      },
      {
        field: 'sellingPrice',
        order: 'asc',
        isForVariation: 'true',
        isForParent: 'false',
      },
    ],
    page: pageIndex,
    productsCount: pageSize,
    currency: 'INR',
    type: type,
    attributeFacetValuesLimit: WIZZY_CONSTANTS.FACETS_VALUE_LIMIT,
    includeCategoriesFacetForPage: 'true',
    inStock: [true],
    categories: categories,
    swatch: [{ key: 'sizes' }],
    getAllVariants: false,
  };
  let options: any = undefined;
  if (headers) {
    options = { headers };
  }
  if (useNextCache) {
    options.useNextCache = useNextCache;
  }

  const res = await wizzyFilter(requestBody, options);
  const response = res?.payload;
  const products = structureProductsData(response?.result) || [];

  return products;
};

export const getCartRecommendedProductsWizzy = async ({
  cartItems,
}: CartRecommendationsPropTypes) => {
  const productsInCart: any = cartItems
    ?.map(item => [item.sku, item.extension_attributes?.parent_sku])
    .flat()
    .filter(Boolean);
  const topCartItems = cartItems?.slice(0, Math.min(3, cartItems.length));
  let recommendedProductIds: string[] = [];
  let recommendedCategories: string[] = [];
  if (topCartItems) {
    recommendedProductIds = topCartItems
      .map(product =>
        product?.extension_attributes?.product_links?.map(
          link => link?.extension_attributes?.id
        )
      )
      .flat();

    recommendedCategories = topCartItems
      .map(product => product?.extension_attributes?.recommended_categories)
      .filter(categories => categories !== undefined)
      .flat();
  }

  try {
    const [response, additionalCartItemsCarousel] = await Promise.all([
      recommendedProductIds.length > 0
        ? getProductsByIds(recommendedProductIds, {
            headers: {
              'x-store-id':
                storage.get('wizzy_store_id') || process.env.WIZZY_STORE_ID,
              'x-api-key':
                storage.get('wizzy_api_key') || process.env.WIZZY_API_KEY,
              'x-wizzy-sessionId': sessionStorage?.getItem('wizzy_sessionId'),
              'x-wizzy-userId': storage.get('wizzy_userId'),
            },
          })
        : Promise.resolve([]),
      getMagentoCarouselsGraphQl({
        carouselType: MAGENTO_GRAPHQL_CAROUSEL_TYPES.CART_RECOMMENDATIONS,
        store_code: storage.get('store_code') || DEFAULT_STORE_CODE,
        token: storage.get('token') || undefined,
      }),
    ]);

    const urlKeys = (additionalCartItemsCarousel || [])
      .map((c: any) => c?.category_url_key)
      .filter(Boolean);

    // Prepare headers for Wizzy calls
    const wizzyHeaders = {
      'x-store-id': storage.get('wizzy_store_id') || process.env.WIZZY_STORE_ID,
      'x-api-key': storage.get('wizzy_api_key') || process.env.WIZZY_API_KEY,
      'x-wizzy-sessionId': sessionStorage?.getItem('wizzy_sessionId'),
      'x-wizzy-userId': storage.get('wizzy_userId'),
    };

    // Fetch Wizzy PLP data concurrently for all carousel categories
    const wizzyPromises = urlKeys.map((key: string) =>
      getWizzyPlpData({
        urlKey: key,
        headers: wizzyHeaders,
        inStock: [true],
      }).then((res: any) => ({ urlKey: key, data: res }))
    );
    const wizzyResults = (await Promise.all(wizzyPromises)).filter(Boolean);

    const filteredCartRecommendations = response?.filter(
      (product: any) => product?.stock_status === 'IN_STOCK'
    );

    if (
      recommendedCategories?.length > 0 &&
      recommendedProductIds?.length < 4
    ) {
      const extraRecommendedProducts = await getWizzyRecommededData({
        categories: recommendedCategories,
        pageIndex: 1,
        pageSize: 8,
        headers: wizzyHeaders,
      });
      filteredCartRecommendations.push(...extraRecommendedProducts);
    }

    // Helper: remove in-cart SKUs and dedupe by sku
    const removeInCartAndDedupe = (products: any[]) =>
      (products || [])
        .filter((p: any) => !productsInCart?.includes(p.sku))
        .filter(
          (product: any, index: number, self: any[]) =>
            index === self.findIndex(p => p.sku === product.sku)
        );

    // Build final object keyed by urlKey
    const result: Record<string, { name: string; products: any[] }> = {};

    if (wizzyResults.length === 0) {
      // No carousel categories — fall back to a single recommended key
      const merged = removeInCartAndDedupe(
        [...(filteredCartRecommendations || [])].slice(0, 12)
      ).filter((p: any) => p?.stock_status === 'IN_STOCK');
      if (merged.length === 0) return {};
      result.recommended = { name: 'Recommended', products: merged };
      return result;
    }

    // For the first category, merge its products with filteredCartRecommendations (limited)
    const first = wizzyResults[0];
    const firstProducts = first?.data?.products || [];
    const mergedFirst = removeInCartAndDedupe([
      ...firstProducts,
      ...((filteredCartRecommendations || []).slice(0, 12) as any[]),
    ]).filter((p: any) => p?.stock_status === 'IN_STOCK');
    if (mergedFirst.length > 0) {
      result[first.urlKey] = {
        name: first?.data?.metaData?.name || '',
        products: mergedFirst,
      };
    }

    // For remaining categories, include their products as-is (but filter out in-cart and dedupe within list)
    for (let i = 1; i < wizzyResults.length; i++) {
      const item = wizzyResults[i];
      const prods = removeInCartAndDedupe(item?.data?.products || []).filter(
        (p: any) => p?.stock_status === 'IN_STOCK'
      );
      if (prods.length > 0) {
        result[item.urlKey] = {
          name: item?.data?.metaData?.name || '',
          products: prods,
        };
      }
    }

    return result;
  } catch (error) {
    logger.log('Error fetching recommended products in cart', error);
    return null;
  }
};
